// client/components/widgets/WidgetRail.jsx
// ✅ Widget Rail mit Cross-Rail Drag & Drop (dnd-kit)
import React, { memo, useCallback } from "react";
import { Plus } from "lucide-react";
import { tw, cn } from "../../utils/themeClasses.js";
import WidgetShell from "./WidgetShell.jsx";
import AppButton from "../ui/AppButton.jsx";
import { useAuth } from "../ui/AuthContext.jsx";

// dnd-kit imports
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

/**
 * Sortable Widget Item - Wrapper für Drag & Drop
 */
const SortableWidget = memo(function SortableWidget({
  widget,
  onMinimize,
  onRemove,
  railWidth,
  lockRemove = false,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: widget.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || "transform 200ms ease",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={cn(
        "transition-all duration-200",
        isDragging && "opacity-30 scale-[0.98]",
        isOver && !isDragging && "ring-2 ring-blue-500/40 rounded-2xl shadow-xl"
      )}
    >
      <WidgetShell
        widgetId={widget.id}
        isMinimized={widget.minimized}
        onMinimize={() => onMinimize(widget.id)}
        onRemove={() => onRemove(widget.id)}
        railWidth={railWidth}
        compact={true}
        dragHandleProps={listeners}
        lockRemove={lockRemove}
      />
    </div>
  );
});

/**
 * WidgetRail - Vertikaler Container für Widgets mit Cross-Rail Drag & Drop
 * DndContext ist im WidgetLayout für Cross-Rail Support
 */
const WidgetRail = memo(function WidgetRail({
  position = "left",
  widgets = [],
  width = 260,
  maxWidgets = 12,
  onAddWidget,
  onRemoveWidget,
  onToggleMinimize,
  isDragging = false,
}) {
  const { needAcademy } = useAuth();
  const { setNodeRef, isOver } = useDroppable({
    id: `droppable-${position}`,
  });
  const handleRemove = useCallback(
    (widgetId) => {
      onRemoveWidget?.(widgetId);
    },
    [onRemoveWidget]
  );
  const handleMinimize = useCallback(
    (widgetId) => {
      onToggleMinimize?.(widgetId);
    },
    [onToggleMinimize]
  );
  const canAddMore = widgets.length < maxWidgets;
  const widgetIds = widgets.map((w) => w.id);
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex flex-col gap-1.5 min-h-[120px] rounded-lg transition-all duration-200",
        isOver && "bg-blue-500/8 ring-1 ring-blue-500/20",
        isDragging && !isOver && "ring-1 ring-white/[0.06]"
      )}
      style={{
        width: `${width}px`,
      }}
      data-widget-rail={position}
    >
      {canAddMore && (
        <AppButton
          type="button"
          variant="ghost"
          onClick={() => onAddWidget?.(position)}
          className={cn(
            "flex items-center justify-center gap-1",
            "w-full py-1.5 rounded-2xl border border-dashed",
            "transition-all duration-200 text-[11px] font-medium",
            "border-white/15 text-blue-200/70 hover:text-blue-100",
            "hover:border-blue-500/40 hover:shadow-lg hover:bg-[#0b1324]/50"
          )}
        >
          <Plus size={11} strokeWidth={2} />
          Widget hinzufügen
        </AppButton>
      )}

      <SortableContext items={widgetIds} strategy={verticalListSortingStrategy}>
        {widgets.map((widget) => (
          <SortableWidget
            key={widget.id}
            widget={widget}
            onMinimize={handleMinimize}
            onRemove={handleRemove}
            railWidth={width}
            lockRemove={needAcademy && widget.id === "academy-progress"}
          />
        ))}
      </SortableContext>

      {widgets.length === 0 && (
        <div
          className={cn(
            "flex flex-col items-center justify-center py-5 px-2",
            "rounded-lg border-2 border-dashed text-center transition-colors",
            isOver ? "border-blue-400/50 bg-blue-500/10" : "border-white/10"
          )}
        >
          <p className={cn("text-xs", isOver ? "text-blue-400" : tw.textMuted(true))}>
            {isOver ? "Hier ablegen" : "Widgets hierher ziehen"}
          </p>
        </div>
      )}

      {!canAddMore && (
        <p className={cn("text-[10px] text-center", tw.textFaint(true))}>
          Maximum erreicht ({maxWidgets})
        </p>
      )}
    </div>
  );
});
export default WidgetRail;
